import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';

const DARK_BLUE = '#003479';
const PRIMARY   = '#20C997';

const TYPE_META = {
  assignment_posted:   { color: '#3b82f6', label: 'Assignment Posted',   icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  feed_item:           { color: '#8b5cf6', label: 'Feed Update',         icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  class_scheduled:     { color: '#10b981', label: 'Class Scheduled',     icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  assignment_graded:   { color: '#f59e0b', label: 'Assignment Graded',   icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
  class_starting_soon: { color: '#ef4444', label: 'Class Starting Soon', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
};

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const TABS = ['All', 'Unread', 'Assignment Posted', 'Class Scheduled', 'Assignment Graded', 'Feed Update'];

const TAB_FILTER = {
  'All': null,
  'Unread': 'unread',
  'Assignment Posted': 'assignment_posted',
  'Class Scheduled': 'class_scheduled',
  'Assignment Graded': 'assignment_graded',
  'Feed Update': 'feed_item',
};

export default function StudentNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [activeTab, setActiveTab]         = useState('All');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/notifications?limit=100');
      setNotifications(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleMarkRead = async (id, link) => {
    try {
      await api.patch(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {}
    if (link) navigate(link);
  };

  const handleMarkAll = async () => {
    try {
      await api.patch('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  // Filter by tab
  const filter = TAB_FILTER[activeTab];
  const visible = notifications.filter(n => {
    if (!filter) return true;
    if (filter === 'unread') return !n.is_read;
    return n.type === filter;
  });

  const unreadTotal = notifications.filter(n => !n.is_read).length;

  return (
    <Layout>
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: DARK_BLUE, margin: 0 }}>Notifications</h1>
          {unreadTotal > 0 && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{unreadTotal} unread</p>
          )}
        </div>
        {unreadTotal > 0 && (
          <button
            onClick={handleMarkAll}
            style={{ fontSize: 13, fontWeight: 600, color: PRIMARY, background: 'none', border: `1.5px solid ${PRIMARY}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(tab => {
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                background: active ? DARK_BLUE : '#f3f4f6',
                color: active ? '#fff' : '#374151',
                transition: 'all 0.15s',
              }}
            >
              {tab}
              {tab === 'Unread' && unreadTotal > 0 && (
                <span style={{ marginLeft: 6, background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>
                  {unreadTotal}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
          <svg width={48} height={48} fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p style={{ fontSize: 15, margin: 0 }}>No notifications here</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: 14, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          {visible.map((n, i) => {
            const meta = TYPE_META[n.type] || { color: '#6b7280', label: n.type, icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' };
            return (
              <button
                key={n.id}
                onClick={() => handleMarkRead(n.id, n.link)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '16px 20px',
                  background: n.is_read ? '#fff' : 'rgba(32,201,151,0.05)',
                  border: 'none',
                  borderBottom: i < visible.length - 1 ? '1px solid #f3f4f6' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? '#fff' : 'rgba(32,201,151,0.05)'; }}
              >
                {/* Icon */}
                <div style={{ width: 42, height: 42, borderRadius: 10, background: `${meta.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                  <svg width={20} height={20} fill="none" stroke={meta.color} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
                  </svg>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: meta.color, background: `${meta.color}14`, borderRadius: 6, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {meta.label}
                    </span>
                    {!n.is_read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#20C997', flexShrink: 0 }} />}
                  </div>
                  <p style={{ fontSize: 14, fontWeight: n.is_read ? 500 : 700, color: '#111827', margin: '0 0 4px', lineHeight: 1.4 }}>
                    {n.title}
                  </p>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px', lineHeight: 1.5 }}>
                    {n.message}
                  </p>
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{formatDate(n.created_at)}</p>
                </div>

                {/* Arrow */}
                {n.link && (
                  <svg width={16} height={16} fill="none" stroke="#9ca3af" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 12 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
    </Layout>
  );
}
